import type { SubtitleStyleOptions } from '../language-menu/language-menu.types';

import classNames from 'classnames';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useElementsVisibilities } from './hooks/use-elements-visibilities';
import { useKeyboardShortcuts } from './hooks/use-keyboard-shortcuts';
import { COLORS } from './hooks/use-player-colors-styles';
import { UserInterfaceProps } from './user-interface.types';
import { ChaptersMenu } from '../../components/chapters-menu/chapters-menu';
import { ChaptersMenuButton } from '../../components/chapters-menu/chapters-menu-button';
import { LiveBadge } from '../../components/live-badge/live-badge';
import { MobileOverlay } from '../../components/mobile-overlay/mobile-overlay';
import { PlayerRefProvider } from '../../contexts/player-ref-context';
import { useDynamicSize } from '../../hooks/use-dynamic-size';
import { useHover } from '../../hooks/use-hover';
import { useKeyboardNavigating } from '../../hooks/use-keyboard-navigating';
import { useMouseMoving } from '../../hooks/use-mouse-moving';
import { AudioOverlay } from '../audio-overlay/audio-overlay';
import { FullscreenButton } from '../fullscreen-button/fullscreen-button';
import { LanguageMenu } from '../language-menu/language-menu';
import { LanguageMenuButton } from '../language-menu/language-menu-button';
import { LoopButton } from '../loop-button/loop-button';
import { Overlay } from '../overlay/overlay';
import { PlayButton } from '../play-button/play-button';
import { RelatedVideos } from '../related-videos/related-videos';
import { SettingsMenu } from '../settings-menu/settings-menu';
import { SettingsMenuButton } from '../settings-menu/settings-menu-button';
import { Spinner } from '../spinner/spinner';
import { StepBackButton, StepForwardButton } from '../step-buttons/step-buttons';
import { Subtitles } from '../subtitles/subtitles';
import { Timeline } from '../timeline/timeline';
import { Timer } from '../timer/timer';
import { TopBar } from '../top-bar/top-bar';
import { TranscriptMenu } from '../transcript/transcript-menu';
import { TranscriptMenuButton } from '../transcript/transcript-menu-button';
import { Trimmer } from '../trimmer/trimmer';
import { Volume } from '../volume/volume';
import { WatermarkOverlay } from '../watermark-overlay/watermark-overlay';

import styles from './user-interfaces.module.css';

export function UserInterface({
  elementVisibilitySettings = {},
  breakpoints = [],
  isMobile = false,
  isPlaying = false,
  isEnded = false,
  logoUrl = '',
  title = '',
  currentTime = 0,
  totalTime = 0,
  bufferPercentage = 0,
  isLooping = false,
  volume = 1,
  isFullscreen = false,
  liveUI = false,
  audioOnly = false,
  audioCoverSource,
  subtitlesEnabled = false,
  subtitle = '',
  subtitleOptionId = '',
  subtitleOptions = [],
  transcript,
  selectedTranscriptLanguage,
  onSelectTranscriptLanguage,
  transcriptLanguages,
  onTranscriptItemClick,
  onTranscriptButtonClick,
  transcriptMenuIsOpen,
  setTranscriptMenuIsOpen,
  qualityId = '',
  qualities = [],
  autoQualityOptionAvailable = true,
  playbackSpeedId = '',
  playbackSpeeds = [],
  storyboard,
  chapters,
  downloadSources,
  watermark,
  isTrimmable = false,
  trimStartTime = 0,
  trimEndTime = 0,
  isMuted = false,
  onPlaybackToggle,
  onTimeChange,
  onTimeDeltaChange,
  onLoopButtonClick,
  onVolumeChange,
  onVolumeDeltaChange,
  onMuteChange,
  onFullscreenToggle,
  onSetSubtitle,
  onSetQuality,
  onSetPlaybackSpeed,
  onTrimStartTimeChange,
  onTrimEndTimeChange,
  audioTrackId = '',
  audioTracks = [],
  onSetAudioTrack,
  onOverlayVisibilityChange,
  onChapterClick,
  relatedVideos,
  onRelatedVideoClick,
  theme,
  hideControlsBeforePlay,
}: UserInterfaceProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const isMouseMovingOverWrapper = useMouseMoving(wrapperRef);
  const isKeyboardFocusOverTopBar = useKeyboardNavigating(topBarRef);
  const isKeyboardFocusOverBottomBar = useKeyboardNavigating(bottomBarRef);
  const isBottomBarHovered = useHover(bottomBarRef);
  const { width: wrapperWidth } = useDynamicSize(wrapperRef);
  const {
    showLogo,
    showTitle,
    showTopbar,
    showPlayButton,
    showTimeline,
    showVolumeInput,
    showVolumeSlider,
    showVideoLength,
    showLoopButton,
    showSubtitles,
    showSettingsButton,
    showFullscreenButton,
    showPlaybackSpeed,
    showBigPlayButton,
    showStepButtons,
    showChaptersButton,
    showTranscriptButton,
    showTranscriptDownload,
    showRelatedVideos: relatedVideosAvailable,
    verticalVolumeSlider,
    smallScreen,
  } = useElementsVisibilities(wrapperWidth, elementVisibilitySettings, breakpoints);
  const isUsingTranscriptSplitView = !!onTranscriptButtonClick;
  const hasPlayedRef = useRef(false);
  hasPlayedRef.current = hasPlayedRef.current || isPlaying;

  const [openedMenu, setOpenedMenu] = useState<Menus | null>(null);
  const closeMenu = useCallback(() => {
    setOpenedMenu(null);
    if (!isUsingTranscriptSplitView) {
      setTranscriptMenuIsOpen(false);
    }
  }, [isUsingTranscriptSplitView, setTranscriptMenuIsOpen]);
  const settingsMenuIsOpen = openedMenu === Menus.settings;
  const languageMenuIsOpen = openedMenu === Menus.language;
  const chaptersMenuIsOpen = openedMenu === Menus.chapters;
  const dlSourcesMenuMenuIsOpen = openedMenu === Menus.download;

  const showTranscriptMenu = transcriptMenuIsOpen && (!isUsingTranscriptSplitView || (isFullscreen && !openedMenu));
  const menuIsOpen =
    chaptersMenuIsOpen || languageMenuIsOpen || settingsMenuIsOpen || dlSourcesMenuMenuIsOpen || transcriptMenuIsOpen;

  const defaultSubtitleStyleOptions: SubtitleStyleOptions = useMemo(
    () => ({
      backgroundColor: theme === 'light' ? COLORS.white : COLORS.black,
      backgroundTransparency: '25%',
      fontColor: theme === 'light' ? COLORS.black : COLORS.white,
      fontFamily: 'Montserrat',
      fontSize: '1',
    }),
    [theme]
  );
  const [subtitleStyleOptions, setSubtitleStyleOptions] = useState<SubtitleStyleOptions>(defaultSubtitleStyleOptions);
  const resetSubtitleStyleOptionsAvailable = useMemo(
    () => JSON.stringify(defaultSubtitleStyleOptions) !== JSON.stringify(subtitleStyleOptions),
    [defaultSubtitleStyleOptions, subtitleStyleOptions]
  );
  const resetSubtitleStyleOptions = useMemo(
    () => (resetSubtitleStyleOptionsAvailable ? () => setSubtitleStyleOptions(defaultSubtitleStyleOptions) : undefined),
    [defaultSubtitleStyleOptions, resetSubtitleStyleOptionsAvailable]
  );

  const lastKeyboardAction = useKeyboardShortcuts(
    wrapperRef,
    onVolumeDeltaChange,
    onTimeDeltaChange,
    onMuteChange,
    isMuted,
    menuIsOpen,
    onPlaybackToggle,
    onFullscreenToggle
  );

  const toggleDlSourcesMenu = useCallback(() => {
    setOpenedMenu((state) => (state === Menus.download ? null : Menus.download));
    if (!isUsingTranscriptSplitView) {
      setTranscriptMenuIsOpen(false);
    }
  }, [isUsingTranscriptSplitView, setTranscriptMenuIsOpen]);

  const toggleSettingsMenu = useCallback(() => {
    setOpenedMenu((state) => (state === Menus.settings ? null : Menus.settings));
    if (!isUsingTranscriptSplitView) {
      setTranscriptMenuIsOpen(false);
    }
  }, [isUsingTranscriptSplitView, setTranscriptMenuIsOpen]);

  const toggleLanguageMenu = useCallback(() => {
    setOpenedMenu((state) => (state === Menus.language ? null : Menus.language));
    if (!isUsingTranscriptSplitView) {
      setTranscriptMenuIsOpen(false);
    }
  }, [isUsingTranscriptSplitView, setTranscriptMenuIsOpen]);

  const toggleChaptersMenu = useCallback(() => {
    setOpenedMenu((state) => (state === Menus.chapters ? null : Menus.chapters));
    if (!isUsingTranscriptSplitView) {
      setTranscriptMenuIsOpen(false);
    }
  }, [isUsingTranscriptSplitView, setTranscriptMenuIsOpen]);

  const toggleTranscriptMenu = useCallback(() => {
    setTranscriptMenuIsOpen((state) => !state);
    if (!isUsingTranscriptSplitView) {
      setOpenedMenu(null);
    }
  }, [isUsingTranscriptSplitView, setTranscriptMenuIsOpen]);

  const closeTranscriptMenu = useCallback(() => {
    setTranscriptMenuIsOpen(false);
  }, [setTranscriptMenuIsOpen]);

  const onBackButtonClick = useCallback(() => {
    onTimeDeltaChange?.(-10000);
  }, [onTimeDeltaChange]);

  const onForwardButtonClick = useCallback(() => {
    onTimeDeltaChange?.(10000);
  }, [onTimeDeltaChange]);

  const controlsAreHiddenBeforePlay = hideControlsBeforePlay && !hasPlayedRef.current;
  const controlsAreHidden =
    controlsAreHiddenBeforePlay ||
    (isPlaying && !isMouseMovingOverWrapper && !isKeyboardFocusOverBottomBar && !isKeyboardFocusOverTopBar);
  const logoIsVisible = showLogo && logoUrl !== '';
  const titleIsVisible = showTitle && title !== '';
  const downloadSourcesExist = downloadSources && downloadSources.length > 0;
  const timelineIsVisible = showTimeline && totalTime > 0;
  const showRelatedVideos = !!relatedVideos?.length && isEnded && relatedVideosAvailable;
  const topBarIsVisible = showTopbar && !showRelatedVideos && (logoIsVisible || titleIsVisible || downloadSourcesExist);
  const subtitlesAreVisible = subtitlesEnabled && subtitle !== '';
  const subtitlesHaveSpaceForBottomBar = isBottomBarHovered || !controlsAreHidden;
  const languageMenuIsVisible =
    showSubtitles && ((audioTracks && audioTracks?.length > 0) || subtitleOptions.length > 0);
  const chaptersMenuIsVisible = showChaptersButton && chapters && chapters?.length > 0;
  const transcriptMenuIsVisible = showTranscriptButton && (transcript?.cues?.length ?? 0) > 0;

  useEffect(() => {
    onOverlayVisibilityChange?.(controlsAreHidden);
  }, [controlsAreHidden, onOverlayVisibilityChange]);

  return (
    <PlayerRefProvider playerRef={wrapperRef}>
      <div
        ref={wrapperRef}
        className={classNames(styles.wrapper, { smallScreen }, { menuIsOpen }, { [theme?.toString() || 'dark']: true })}
        data-testid="player-wrapper"
        tabIndex={-1}
      >
        <Spinner />

        <WatermarkOverlay watermark={watermark} />

        {topBarIsVisible && (
          <TopBar
            ref={topBarRef}
            isHidden={controlsAreHidden}
            logoUrl={logoIsVisible ? logoUrl : ''}
            title={titleIsVisible ? title : ''}
            downloadSources={downloadSources}
            downloadSourcesMenuOpen={dlSourcesMenuMenuIsOpen}
            onDownloadSourcesMenuButtonClick={toggleDlSourcesMenu}
            onDownloadSourcesMenuClose={closeMenu}
          />
        )}

        {showRelatedVideos && (
          <RelatedVideos videos={relatedVideos} onClick={onRelatedVideoClick} smallScreen={smallScreen} />
        )}

        {audioOnly && <AudioOverlay coverSource={audioCoverSource} isPlaying={isPlaying} isStopped={isEnded} />}

        <div
          ref={bottomBarRef}
          className={classNames(styles.bottomBar, { [styles.hidden]: !isTrimmable && controlsAreHidden })}
          data-testid="bottom-bar"
        >
          {timelineIsVisible && (
            <Suspense fallback={null}>
              {isTrimmable ? (
                <Trimmer
                  wrapper={wrapperRef.current}
                  isPlaying={isPlaying}
                  startTime={trimStartTime}
                  endTime={trimEndTime}
                  currentTime={currentTime}
                  totalTime={totalTime}
                  storyboard={storyboard}
                  onChange={onTimeChange}
                  onTrimStartTimeChange={onTrimStartTimeChange}
                  onTrimEndTimeChange={onTrimEndTimeChange}
                />
              ) : (
                <div>
                  {showVideoLength && !liveUI && (
                    <Timer
                      className={classNames('left-side', styles.timelineTimerTop)}
                      currentTime={currentTime}
                      totalTime={totalTime}
                    />
                  )}
                  <Timeline
                    currentTime={currentTime}
                    totalTime={totalTime}
                    bufferPercentage={bufferPercentage}
                    liveUI={liveUI}
                    isMobile={isMobile}
                    isSmallScreen={smallScreen}
                    storyboard={storyboard}
                    onChange={onTimeChange}
                    chapters={chapters}
                  />
                </div>
              )}
            </Suspense>
          )}

          <div className={styles.buttonsSection} data-testid="buttons-bottom-bar">
            {showPlayButton && <PlayButton className="left-side" isPlaying={isPlaying} onClick={onPlaybackToggle} />}
            {showVolumeInput && (
              <Volume
                className="left-side"
                value={volume}
                isMuted={isMuted}
                showVolumeSlider={showVolumeSlider}
                verticalVolumeSlider={verticalVolumeSlider}
                onChange={onVolumeChange}
                onMuteChange={onMuteChange}
              />
            )}
            {showStepButtons && (
              <>
                <StepBackButton className="left-side" onClick={onBackButtonClick} />
                <StepForwardButton className="left-side" onClick={onForwardButtonClick} />
              </>
            )}
            {showVideoLength &&
              (!liveUI ? (
                <Timer
                  className={classNames('left-side', styles.timelineTimer)}
                  currentTime={currentTime}
                  totalTime={totalTime}
                />
              ) : (
                <LiveBadge className="left-side" />
              ))}
            {showLoopButton && <LoopButton className="right-side" isLooping={isLooping} onClick={onLoopButtonClick} />}

            {transcriptMenuIsVisible && (
              <>
                <TranscriptMenuButton
                  className="right-side"
                  isActive={transcriptMenuIsOpen}
                  onClick={toggleTranscriptMenu}
                />
                <TranscriptMenu
                  transcript={transcript}
                  isOpen={showTranscriptMenu}
                  onClose={closeTranscriptMenu}
                  currentTime={currentTime}
                  languages={transcriptLanguages}
                  selectedLanguage={selectedTranscriptLanguage}
                  onLanguageChange={onSelectTranscriptLanguage}
                  onTranscriptItemClick={onTranscriptItemClick}
                  showTranscriptDownload={showTranscriptDownload}
                />
              </>
            )}

            {chaptersMenuIsVisible && (
              <>
                <ChaptersMenuButton className="right-side" isActive={chaptersMenuIsOpen} onClick={toggleChaptersMenu} />
                <ChaptersMenu
                  options={chapters}
                  isOpen={chaptersMenuIsOpen}
                  onClose={closeMenu}
                  onChapterClick={onChapterClick}
                  currentTime={currentTime}
                />
              </>
            )}
            {languageMenuIsVisible && (
              <>
                <LanguageMenuButton className="right-side" isActive={languageMenuIsOpen} onClick={toggleLanguageMenu} />
                <LanguageMenu
                  isOpen={languageMenuIsOpen}
                  chosenSubtitleId={subtitleOptionId}
                  subtitles={subtitleOptions}
                  subtitlesEnabled={subtitlesEnabled}
                  onSetSubtitle={onSetSubtitle}
                  onClose={closeMenu}
                  audioTracks={audioTracks}
                  onSetAudioTrack={onSetAudioTrack}
                  chosenAudioTrackId={audioTrackId}
                  subtitleStyleOptions={subtitleStyleOptions}
                  setSubtitleStyleOptions={setSubtitleStyleOptions}
                  resetSubtitleStyleOptions={resetSubtitleStyleOptions}
                />
              </>
            )}

            {showSettingsButton && (
              <>
                <SettingsMenuButton className="right-side" isActive={settingsMenuIsOpen} onClick={toggleSettingsMenu} />
                <SettingsMenu
                  isOpen={settingsMenuIsOpen}
                  chosenQualityId={qualityId}
                  qualities={qualities}
                  autoQualityOptionAvailable={autoQualityOptionAvailable}
                  chosenPlaybackSpeedId={playbackSpeedId}
                  playbackSpeeds={playbackSpeeds}
                  hidePlaybackSpeed={!showPlaybackSpeed}
                  onSetQuality={onSetQuality}
                  onSetPlaybackSpeed={onSetPlaybackSpeed}
                  onClose={closeMenu}
                />
              </>
            )}

            {showFullscreenButton && (
              <FullscreenButton className="right-side" isFullscreen={isFullscreen} onClick={onFullscreenToggle} />
            )}
          </div>
        </div>

        {subtitlesAreVisible && (
          <Subtitles
            withSpaceForBottomBar={subtitlesHaveSpaceForBottomBar}
            wrapperWidth={wrapperWidth}
            subtitleStyleOptions={subtitleStyleOptions}
            content={subtitle}
          ></Subtitles>
        )}

        {!showRelatedVideos ? (
          !isMobile ? (
            <Overlay
              key={showBigPlayButton ? 'big-play-button' : 'big-play-button-disabled'} // To force re-mount when showBigPlayButton changes
              isPlaying={isPlaying}
              isEnded={isEnded}
              isTrimmable={isTrimmable}
              volume={volume}
              isMuted={isMuted}
              wrapperRef={wrapperRef}
              onClick={onPlaybackToggle}
              lastKeyboardAction={lastKeyboardAction}
              enableBigPlayButton={showBigPlayButton}
            />
          ) : (
            <MobileOverlay
              isPlaying={isPlaying}
              liveUI={false}
              isHidden={controlsAreHidden}
              onPlayClick={onPlaybackToggle}
              onBackButtonClick={onBackButtonClick}
              onForwardButtonClick={onForwardButtonClick}
              showBigPlayButton={!controlsAreHidden || controlsAreHiddenBeforePlay}
            />
          )
        ) : null}
      </div>
    </PlayerRefProvider>
  );
}

enum Menus {
  settings = 'settings',
  language = 'language',
  chapters = 'chapters',
  download = 'download',
  transcript = 'transcript',
}
